import React, { useEffect, useMemo, useState, useContext } from "react";
import { FieldConfigurationContext } from "../../../../../lib/context/FieldConfigurationContext";
import TextInput from "../../../../common/TextInput";
import TextArea from "../../../../common/TextArea";
import SelectInput from "../../../../common/SelectInput";
import Validation from "../../../../common/Validation";
import DetailHeader from "../../components/DetailHeader";
import FormFooter from "../../components/FormFooter";
import { SERVICE_MANAGEMENT_ENABLED } from "../../roleUtils";
import {
  getActiveCompanyOptions,
  SELECTED_COMPANY_STORAGE_KEY,
} from "../AccessGroupsList";
import "../RoleDetail.css";
import "../RoleForm/RoleForm.css";

export default function AccessGroupForm({ group, onSave, onCancel }) {
  const { createGroup, updateGroup, companies, licenses, fetchLicenses } =
    useContext(FieldConfigurationContext);

  const [formData, setFormData] = useState({
    name: group?.name || "",
    description: group?.description || "",
  });
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const isEditing = !!group;

  useEffect(() => {
    fetchLicenses();
  }, [fetchLicenses]);

  const licensesLoading = licenses?.loading;
  const hasFieldLicense = [
    "time",
    "expenses",
    "projectManagement",
    ...(SERVICE_MANAGEMENT_ENABLED ? ["serviceManagement"] : []),
  ].some((key) => (licenses?.data?.[key]?.limit || 0) > 0);

  const [groupType, setGroupType] = useState(group?.group_type || "app");
  const [groupTypeTouched, setGroupTypeTouched] = useState(false);

  useEffect(() => {
    if (isEditing || groupTypeTouched || licensesLoading) return;
    setGroupType(hasFieldLicense ? "app" : "api");
  }, [isEditing, groupTypeTouched, hasFieldLicense, licensesLoading]);

  const companyOptions = useMemo(
    () => getActiveCompanyOptions(companies),
    [companies.data, companies.codesMap, companies.enabledData]
  );
  const isMultiCompany = !isEditing && companyOptions.length > 1;

  const editingCompanyLabel = useMemo(() => {
    if (!isEditing || !group?.company_uuid) return "";
    const name = companies.data[group.company_uuid] || group.company_uuid;
    const code = companies.codesMap?.[group.company_uuid];
    return code != null ? `${name} (${code})` : name;
  }, [isEditing, group, companies.data, companies.codesMap]);

  const [selectedCompanyUuid, setSelectedCompanyUuid] = useState(() => {
    const stored = localStorage.getItem(SELECTED_COMPANY_STORAGE_KEY);
    if (stored && companyOptions.some((opt) => opt.value === stored)) {
      return stored;
    }
    return companyOptions[0]?.value || "";
  });

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name?.trim()) {
      newErrors.name = "Name is required.";
    }
    if (!formData.description?.trim()) {
      newErrors.description = "Description is required.";
    }
    if (isMultiCompany && !selectedCompanyUuid) {
      newErrors.company = "Company is required.";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setIsSaving(true);
    setSaveError(null);

    const payload = {
      name: formData.name.trim(),
      description: formData.description?.trim() || "",
      access_codes: group?.access_codes || [],
      group_type: isEditing
        ? group.group_type
        : hasFieldLicense
        ? groupType
        : "api",
    };

    const result = isEditing
      ? await updateGroup(group.uuid, payload, group?.company_uuid)
      : await createGroup(
          payload,
          isMultiCompany ? selectedCompanyUuid : undefined
        );

    setIsSaving(false);

    if (result?.success) {
      if (isMultiCompany && selectedCompanyUuid) {
        localStorage.setItem(SELECTED_COMPANY_STORAGE_KEY, selectedCompanyUuid);
      }
      onSave && onSave(result.data);
    } else {
      setSaveError(result?.error || "Failed to save access group.");
    }
  };

  return (
    <div className="role-form">
      <DetailHeader
        title={
          isEditing ? `Edit Access Group: ${group.name}` : "Add Access Group"
        }
        onBack={onCancel}
        isEditing={false}
      />

      {saveError && (
        <div style={{ padding: "0 24px", marginTop: "16px" }}>
          <Validation className="danger">{saveError}</Validation>
        </div>
      )}

      <div className="role-form__content">
        <div className="role-form__section role-form__section--info">
          <h3 className="role-form__section-title">Group Information</h3>
          <div className="role-form__fields">
            {isMultiCompany && (
              <div>
                <SelectInput
                  label="Company"
                  required
                  value={selectedCompanyUuid}
                  onChange={(e) => setSelectedCompanyUuid(e.target.value)}
                  options={companyOptions}
                  placeholder="Select a company"
                  disabled={isSaving}
                />
                {errors.company && (
                  <div className="role-form__error">{errors.company}</div>
                )}
              </div>
            )}
            {isEditing && editingCompanyLabel && (
              <div className="role-detail__field">
                <label className="role-detail__label">Company</label>
                <div className="role-detail__value">{editingCompanyLabel}</div>
              </div>
            )}
            <div>
              <TextInput
                label="Name"
                required
                value={formData.name}
                onChange={(e) => handleChange("name", e.target.value)}
                placeholder="Enter group name"
                disabled={isSaving}
              />
              {errors.name && (
                <div className="role-form__error">{errors.name}</div>
              )}
            </div>
            <div>
              <TextArea
                label="Description"
                required
                rows={3}
                value={formData.description}
                onChange={(e) => handleChange("description", e.target.value)}
                placeholder="Enter group description"
                disabled={isSaving}
              />
              {errors.description && (
                <div className="role-form__error">{errors.description}</div>
              )}
            </div>
            {isEditing ? (
              <div className="role-detail__field">
                <label className="role-detail__label">Type</label>
                <div className="role-detail__value">
                  {group.group_type === "api" ? "API" : "Field"}
                </div>
              </div>
            ) : (
              hasFieldLicense && (
                <SelectInput
                  label="Type"
                  value={groupType}
                  onChange={(e) => {
                    setGroupTypeTouched(true);
                    setGroupType(e.target.value);
                  }}
                  options={[
                    { value: "app", label: "Field" },
                    { value: "api", label: "API" },
                  ]}
                  disabled={isSaving}
                />
              )
            )}
          </div>
        </div>
      </div>

      <FormFooter
        onCancel={onCancel}
        onSave={handleSave}
        saveDisabled={isSaving}
        saveLabel={isSaving ? "Saving..." : "Save"}
      />
    </div>
  );
}
